import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  MapPin, Play, Square, Camera, Trash2, ArrowRight, Loader2, Crosshair, Layers,
  Mic, MicOff, UserPlus,
} from "lucide-react";

// Marker icon fix
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Custom pin colors by state
const iconFor = (converted) => L.divIcon({
  className: "",
  html: `<div style="
    background:${converted ? "#10b981" : "#ef4444"};
    width:22px;height:22px;border-radius:50% 50% 50% 0;
    transform:rotate(-45deg);border:2px solid #fff;
    box-shadow:0 2px 6px rgba(0,0,0,0.35);
    display:flex;align-items:center;justify-content:center;
  "><span style="transform:rotate(45deg);color:#fff;font-size:10px;font-weight:700;">
    ${converted ? "✓" : "!"}</span></div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 22],
});

const userIcon = L.divIcon({
  className: "",
  html: `<div style="
    background:#2563eb;width:14px;height:14px;border-radius:50%;
    border:3px solid #fff;box-shadow:0 0 0 4px rgba(37,99,235,0.35);
  "></div>`,
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

const DEFAULT_CENTER = [26.1224, -80.1373]; // Fort Lauderdale-ish

// Resize a File → data URL, max width 720px, JPEG quality 0.7
async function resizePhoto(file, maxW = 720) {
  const bmp = await createImageBitmap(file);
  const ratio = Math.min(1, maxW / bmp.width);
  const w = Math.round(bmp.width * ratio);
  const h = Math.round(bmp.height * ratio);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d").drawImage(bmp, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", 0.7);
}

async function reverseGeocode(lat, lng) {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`;
    const r = await fetch(url, { headers: { Accept: "application/json" } });
    const j = await r.json();
    return j.display_name || "";
  } catch { return ""; }
}

function Recenter({ pos }) {
  const map = useMap();
  useEffect(() => {
    if (pos) map.setView(pos, Math.max(map.getZoom(), 15), { animate: true });
  }, [pos, map]);
  return null;
}

const CONDITION_LABELS = {
  vacant: "Vacant",
  overgrown_yard: "Overgrown yard",
  boarded_windows: "Boarded windows",
  roof_damage: "Roof damage",
  peeling_paint: "Peeling paint",
  code_violation_sign: "Code violation sign",
  for_sale_sign: "For Sale sign",
  fsbo_sign: "FSBO sign",
  distressed_general: "Distressed (other)",
};
const ALL_FLAGS = Object.keys(CONDITION_LABELS);

export default function DrivingForDollars() {
  const [pins, setPins] = useState([]);
  const [session, setSession] = useState(null);
  const [pos, setPos] = useState(null); // [lat,lng]
  const [tileType, setTileType] = useState("street"); // street | sat
  const watchIdRef = useRef(null);
  const sessionRef = useRef(null); sessionRef.current = session;

  // ------- Voice-note (hold-to-record) state -------
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const [converting, setConverting] = useState(false);
  const [pendingBreadcrumbs, setPendingBreadcrumbs] = useState([]);
  const [pinDialog, setPinDialog] = useState(null); // {lat,lng,address,photo,notes,flags}
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  // Load pins
  const loadPins = async () => {
    try {
      const { data } = await api.get("/d4d/pins");
      setPins(data || []);
    } catch { /* silent */ }
  };
  useEffect(() => { loadPins(); }, []);

  // Auto-locate on mount (one-shot)
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => setPos([p.coords.latitude, p.coords.longitude]),
      () => {}, { enableHighAccuracy: true, maximumAge: 30000, timeout: 8000 }
    );
    return () => { if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current); };
  }, []);

  // Session tracking
  const startSession = async () => {
    try {
      const { data } = await api.post("/d4d/sessions", {});
      setSession(data);
      setPendingBreadcrumbs([]);
      toast.success("Session started — GPS trail is recording");
      if (!navigator.geolocation) return;
      watchIdRef.current = navigator.geolocation.watchPosition(
        (p) => {
          const lat = p.coords.latitude, lng = p.coords.longitude;
          setPos([lat, lng]);
          setPendingBreadcrumbs((prev) => [
            ...prev,
            { lat, lng, at: new Date().toISOString() },
          ]);
        },
        (err) => console.warn("geo watch error", err),
        { enableHighAccuracy: true, maximumAge: 5000, timeout: 10000 }
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Start failed");
    }
  };

  const stopSession = async () => {
    if (!session) return;
    try {
      if (watchIdRef.current) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      const { data } = await api.patch(`/d4d/sessions/${session.id}`, {
        breadcrumbs: pendingBreadcrumbs,
        end: true,
      });
      const km = (data.distance_m / 1000).toFixed(2);
      toast.success(`Session ended — ${km} km recorded`);
      setSession(null);
      setPendingBreadcrumbs([]);
    } catch { toast.error("End failed"); }
  };

  // Flush breadcrumbs every 20s during an active session
  useEffect(() => {
    if (!session) return;
    const interval = setInterval(async () => {
      if (!sessionRef.current || pendingBreadcrumbs.length === 0) return;
      const batch = pendingBreadcrumbs.slice();
      try {
        await api.patch(`/d4d/sessions/${sessionRef.current.id}`, {
          breadcrumbs: batch, end: false,
        });
        setPendingBreadcrumbs((prev) => prev.slice(batch.length));
      } catch { /* keep in local buffer, retry next tick */ }
    }, 20000);
    return () => clearInterval(interval);
  }, [session, pendingBreadcrumbs]);

  const trailPositions = useMemo(() => {
    const s = pendingBreadcrumbs;
    return s.map((c) => [c.lat, c.lng]);
  }, [pendingBreadcrumbs]);

  const openPinAtCurrent = async () => {
    if (!pos) return toast.error("No GPS fix yet — enable location.");
    const [lat, lng] = pos;
    const addr = await reverseGeocode(lat, lng);
    setPinDialog({ lat, lng, address: addr, notes: "", flags: [], photo: null });
  };

  const openPinAtMapCenter = (map) => {
    const c = map.getCenter();
    reverseGeocode(c.lat, c.lng).then((addr) => {
      setPinDialog({ lat: c.lat, lng: c.lng, address: addr, notes: "", flags: [], photo: null });
    });
  };

  const toggleFlag = (f) => {
    setPinDialog((d) => ({
      ...d,
      flags: d.flags.includes(f) ? d.flags.filter((x) => x !== f) : [...d.flags, f],
    }));
  };

  // ------- Voice-note recording (hold-to-record → Whisper transcript) -------
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      rec.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) audioChunksRef.current.push(ev.data);
      };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        if (blob.size < 500) {
          toast.info("Hold the button to record — release when done");
          return;
        }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("audio", blob, "voice_note.webm");
          const { data } = await api.post("/d4d/transcribe", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const txt = (data.text || "").trim();
          if (txt) {
            setPinDialog((p) => p && {
              ...p,
              notes: p.notes ? p.notes.trim() + " " + txt : txt,
            });
            toast.success("Voice note transcribed");
          } else {
            toast.info("No speech detected — try again");
          }
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Transcription failed");
        } finally { setTranscribing(false); }
      };
      mediaRecorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (err) {
      toast.error("Microphone permission denied");
    }
  };
  const stopRecording = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== "inactive") rec.stop();
    setRecording(false);
  };

  // ------- Convert pin to lead (drops into Driving for $ board) -------
  const convertPinToLead = async () => {
    if (!pinDialog?.id) {
      toast.info("Save the pin first, then click Convert to Lead");
      return;
    }
    setConverting(true);
    try {
      await api.post(`/d4d/pins/${pinDialog.id}/convert-to-lead`);
      toast.success("Lead created in Driving for $ board → Spotted column");
      setPinDialog(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Convert failed");
    } finally { setConverting(false); }
  };


  const onPhotoPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await resizePhoto(file);
      setPinDialog((d) => ({ ...d, photo: url }));
    } catch { toast.error("Photo failed"); }
  };

  const savePin = async () => {
    if (!pinDialog) return;
    setSaving(true);
    try {
      const payload = {
        lat: pinDialog.lat, lng: pinDialog.lng,
        address: pinDialog.address || null,
        notes: pinDialog.notes || null,
        condition_flags: pinDialog.flags,
        photo_base64: pinDialog.photo || null,
        session_id: session?.id || null,
      };
      const { data } = await api.post("/d4d/pins", payload);
      setPins((prev) => [data, ...prev]);
      setPinDialog(null);
      toast.success("Pin dropped");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const deletePin = async (pid) => {
    if (!window.confirm("Delete this pin?")) return;
    try {
      await api.delete(`/d4d/pins/${pid}`);
      setPins((prev) => prev.filter((p) => p.id !== pid));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
  };

  const convertPin = async (pid) => {
    try {
      const { data } = await api.post(`/d4d/pins/${pid}/convert-to-lead`);
      if (data.already_converted) toast.info("Already in CRM");
      else toast.success("Added to CRM Pipeline");
      loadPins();
    } catch { toast.error("Convert failed"); }
  };

  const mapCenter = pos || DEFAULT_CENTER;
  const activeCount = pins.filter((p) => !p.converted_lead_id).length;
  const convertedCount = pins.length - activeCount;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight">
            Driving for Dollars
          </h1>
          <p className="text-xs text-muted-foreground mt-1">
            Drop pins on distressed properties from the field. Snap a photo, then
            convert to a lead with one tap.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">
            <span className="font-medium text-[var(--app-text)]" data-testid="d4d-active-count">{activeCount}</span> open
            {" · "}
            <span data-testid="d4d-converted-count">{convertedCount}</span> in CRM
          </span>
          <Button
            onClick={() => setTileType((t) => (t === "street" ? "sat" : "street"))}
            variant="outline"
            className="rounded-full h-10 px-3"
            data-testid="d4d-toggle-tiles"
            title="Toggle map style"
          >
            <Layers size={14} className="mr-1" /> {tileType === "street" ? "Satellite" : "Street"}
          </Button>
          {session ? (
            <Button
              onClick={stopSession}
              className="rounded-full h-10 px-4 bg-red-600 text-white hover:bg-red-700"
              data-testid="d4d-stop-session"
            >
              <Square size={12} className="mr-2" /> Stop Session
            </Button>
          ) : (
            <Button
              onClick={startSession}
              className="rounded-full h-10 px-4 bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)]"
              data-testid="d4d-start-session"
            >
              <Play size={12} className="mr-2" /> Start Session
            </Button>
          )}
          <Button
            onClick={openPinAtCurrent}
            className="rounded-full h-10 px-4 bg-[var(--app-accent)] text-white hover:bg-[var(--app-black)]"
            data-testid="d4d-drop-pin"
          >
            <MapPin size={12} className="mr-2" /> Drop Pin Here
          </Button>
        </div>
      </div>

      {/* Map */}
      <div
        className="rounded-2xl overflow-hidden border border-[var(--app-line)]"
        style={{ height: "72vh", minHeight: 480 }}
        data-testid="d4d-map-wrap"
      >
        <MapContainer
          center={mapCenter}
          zoom={pos ? 16 : 12}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom
        >
          {tileType === "street" ? (
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap'
            />
          ) : (
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution="Tiles © Esri"
            />
          )}
          <Recenter pos={pos} />
          {pos && <Marker position={pos} icon={userIcon}><Popup>You are here</Popup></Marker>}
          {trailPositions.length > 1 && (
            <Polyline positions={trailPositions} color="#2563eb" weight={4} opacity={0.7} />
          )}
          {pins.map((p) => (
            <Marker
              key={p.id}
              position={[p.lat, p.lng]}
              icon={iconFor(!!p.converted_lead_id)}
            >
              <Popup minWidth={220}>
                <div className="text-xs space-y-1">
                  <div className="font-medium">{p.address || "(no address)"}</div>
                  {p.photo_base64 && (
                    <img
                      src={p.photo_base64}
                      alt=""
                      className="rounded-md w-full object-cover"
                      style={{ maxHeight: 140 }}
                    />
                  )}
                  {(p.condition_flags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {p.condition_flags.map((f) => (
                        <span key={f} className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">
                          {CONDITION_LABELS[f] || f}
                        </span>
                      ))}
                    </div>
                  )}
                  {p.notes && <div className="text-neutral-600">{p.notes}</div>}
                  <div className="flex gap-1 pt-1">
                    {!p.converted_lead_id && (
                      <button
                        onClick={() => convertPin(p.id)}
                        className="text-[10px] uppercase tracking-widest bg-black text-white rounded-full px-2 py-1 hover:bg-neutral-700"
                        data-testid={`d4d-convert-${p.id}`}
                      >
                        <ArrowRight size={10} className="inline mr-0.5" /> To CRM
                      </button>
                    )}
                    {p.converted_lead_id && (
                      <span className="text-[10px] uppercase tracking-widest text-emerald-700">
                        ✓ In CRM
                      </span>
                    )}
                    <button
                      onClick={() => deletePin(p.id)}
                      className="text-[10px] uppercase tracking-widest text-red-600 hover:underline ml-auto"
                      data-testid={`d4d-delete-${p.id}`}
                    >
                      <Trash2 size={10} className="inline" />
                    </button>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Pin editor */}
      <Dialog open={!!pinDialog} onOpenChange={(v) => !v && setPinDialog(null)}>
        <DialogContent
          className="rounded-2xl max-w-lg z-[9999] max-h-[85vh] overflow-y-auto"
          data-testid="d4d-pin-dialog"
        >
          <DialogHeader>
            <DialogTitle>Drop a Pin</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Capture what you see — one tap on a flag is enough. You can enrich later.
            </DialogDescription>
          </DialogHeader>
          {pinDialog && (
            <div className="space-y-3">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">
                {pinDialog.lat.toFixed(6)}, {pinDialog.lng.toFixed(6)}
              </div>
              <div>
                <Label className="text-xs">Address</Label>
                <Input
                  className="mt-1"
                  value={pinDialog.address}
                  onChange={(e) => setPinDialog({ ...pinDialog, address: e.target.value })}
                  data-testid="d4d-pin-address"
                />
              </div>
              <div>
                <Label className="text-xs">What did you see?</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {ALL_FLAGS.map((f) => {
                    const on = pinDialog.flags.includes(f);
                    return (
                      <button
                        key={f}
                        onClick={() => toggleFlag(f)}
                        className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-1 border transition-colors ${
                          on
                            ? "bg-red-600 text-white border-red-600"
                            : "border-[var(--app-line)] text-[var(--app-text)] hover:bg-[var(--app-surface)]"
                        }`}
                        data-testid={`d4d-flag-${f}`}
                      >
                        {CONDITION_LABELS[f]}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs">Notes</Label>
                  <button
                    type="button"
                    onMouseDown={startRecording}
                    onMouseUp={stopRecording}
                    onMouseLeave={() => recording && stopRecording()}
                    onTouchStart={startRecording}
                    onTouchEnd={stopRecording}
                    disabled={transcribing}
                    data-testid="d4d-voice-record-btn"
                    className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border transition-colors ${
                      recording
                        ? "bg-rose-600 text-white border-rose-700 animate-pulse"
                        : transcribing
                        ? "bg-slate-200 text-slate-500 border-slate-300"
                        : "bg-white text-rose-700 border-rose-300 hover:bg-rose-50"
                    }`}
                    title="Hold to record, release to transcribe"
                  >
                    {transcribing ? (
                      <><Loader2 size={11} className="animate-spin" /> Transcribing…</>
                    ) : recording ? (
                      <><Mic size={11} /> Recording — release to stop</>
                    ) : (
                      <><Mic size={11} /> Hold to voice-note</>
                    )}
                  </button>
                </div>
                <Textarea
                  rows={2}
                  className="mt-1"
                  value={pinDialog.notes}
                  onChange={(e) => setPinDialog({ ...pinDialog, notes: e.target.value })}
                  placeholder="Extra observations, e.g. mailbox stuffed, no cars…"
                  data-testid="d4d-pin-notes"
                />
              </div>
              <div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={onPhotoPick}
                  className="hidden"
                  data-testid="d4d-pin-photo-input"
                />
                <Button
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  data-testid="d4d-pin-photo-btn"
                >
                  <Camera size={14} className="mr-2" />
                  {pinDialog.photo ? "Replace photo" : "Add photo"}
                </Button>
                {pinDialog.photo && (
                  <img src={pinDialog.photo} alt="" className="mt-2 rounded-md max-h-40 object-cover w-full" />
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            {pinDialog?.id && (
              <Button
                variant="outline"
                onClick={convertPinToLead}
                disabled={converting}
                data-testid="d4d-convert-to-lead-btn"
                className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                title="Create a lead in Driving for $ → Spotted"
              >
                {converting ? (
                  <><Loader2 size={14} className="animate-spin mr-1.5" /> Converting…</>
                ) : (
                  <><UserPlus size={14} className="mr-1.5" /> Convert to Lead</>
                )}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setPinDialog(null)}
            >Cancel</Button>
            <Button
              onClick={savePin}
              disabled={saving}
              className="rounded-full bg-[var(--app-black)] text-white hover:bg-[var(--app-accent)]"
              data-testid="d4d-save-pin"
            >
              {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : <Crosshair size={14} className="mr-2" />}
              Save Pin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
